import React, { useState, useEffect, useMemo } from "react";
import { Wrench, HardHat, ChevronRight, AlertTriangle, Plus, Trash2, Loader2 } from "lucide-react";
import { getAdminIssues, updateAdminIssue, getContractors, createJob, createAdminIssue, deleteAdminIssue, fetchProperties } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";

const COLUMNS = [
  { key: "reported", label: "Reported", color: "bg-blue-500" },
  { key: "acknowledged", label: "Acknowledged", color: "bg-purple-500" },
  { key: "in-progress", label: "In Progress", color: "bg-amber-500" },
  { key: "resolved", label: "Resolved", color: "bg-green-500" },
];
const STATUSES = COLUMNS.map((c) => c.key);
const nextStatus = { reported: "acknowledged", acknowledged: "in-progress", "in-progress": "resolved", resolved: null };
const input = "w-full h-[44px] px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]";

const AdminMaintenance = () => {
  const { toast } = useToast();
  const [issues, setIssues] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [jobOpen, setJobOpen] = useState(false);
  const [jobIssue, setJobIssue] = useState(null);
  const [jf, setJf] = useState({ contractorId: "", cost: "", description: "" });

  const [properties, setProperties] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [nf, setNf] = useState({ title: "", detail: "", priority: "medium", property: "", contractor: "" });

  const refresh = () => getAdminIssues().then(setIssues).catch(() => setIssues([]));
  useEffect(() => {
    refresh();
    getContractors().then(setContractors).catch(() => setContractors([]));
    fetchProperties().then((d) => setProperties(d.items || d || [])).catch(() => setProperties([]));
  }, []);

  const submitNew = async (e) => {
    e.preventDefault();
    if (!nf.title.trim()) { toast({ title: "Add a title" }); return; }
    setCreating(true);
    try {
      const prop = properties.find((p) => p.id === nf.property);
      const contractor = contractors.find((c) => c.id === nf.contractor);
      const created = await createAdminIssue({
        title: nf.title, detail: nf.detail, priority: nf.priority,
        property: prop ? (prop.address || prop.title) : "", propertyId: prop?.id || null,
        contractor: contractor ? `${contractor.name} — ${contractor.trade}` : null,
      });
      setIssues((xs) => [created, ...xs]);
      setNewOpen(false); setNf({ title: "", detail: "", priority: "medium", property: "", contractor: "" });
      toast({ title: "Issue logged" });
    } catch { toast({ title: "Couldn't create issue" }); }
    finally { setCreating(false); }
  };

  const removeIssue = async (issue) => {
    if (!window.confirm(`Delete "${issue.title}"? This can't be undone.`)) return;
    try { await deleteAdminIssue(issue.id); setIssues((xs) => xs.filter((x) => x.id !== issue.id)); toast({ title: "Issue deleted" }); }
    catch { toast({ title: "Couldn't delete issue" }); }
  };

  const setStatus = async (id, status) => {
    try { const u = await updateAdminIssue(id, { status }); setIssues((xs) => xs.map((x) => x.id === id ? u : x)); toast({ title: `Marked ${status.replace("-", " ")}` }); }
    catch { toast({ title: "Update failed" }); }
  };

  const openJob = (issue) => { setJobIssue(issue); setJf({ contractorId: "", cost: "", description: issue.title || "" }); setJobOpen(true); };
  const submitJob = async (e) => {
    e.preventDefault();
    if (!jf.contractorId) { toast({ title: "Pick a contractor" }); return; }
    try {
      await createJob({ issueId: jobIssue.id, contractorId: jf.contractorId, description: jf.description, cost: Number(jf.cost) || null });
      setJobOpen(false); refresh();
      toast({ title: "Job raised", description: "Tracked in Agency → Contractors → Jobs." });
    } catch (err) { toast({ title: "Couldn't raise job", description: err?.response?.data?.detail || "" }); }
  };

  const grouped = useMemo(() => {
    const g = { reported: [], acknowledged: [], "in-progress": [], resolved: [] };
    issues.forEach((i) => { (g[i.status] || g.reported).push(i); });
    // high priority first within each column
    Object.keys(g).forEach((k) => g[k].sort((a, b) => (b.priority === "high") - (a.priority === "high")));
    return g;
  }, [issues]);

  const openCount = issues.filter((i) => i.status !== "resolved").length;
  const highCount = issues.filter((i) => i.priority === "high" && i.status !== "resolved").length;

  return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] p-6" data-testid="admin-maintenance">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] flex items-center gap-2"><Wrench size={19} className="text-[#b8935f]" /> Maintenance Triage ({issues.length})</h3>
        <div className="flex items-center gap-2">
          <span className="text-[12px] px-3 py-1 rounded-full bg-[#faf7f2] border border-[#f0ece5] text-[#6a6a6a]">{openCount} open</span>
          {highCount > 0 && <span className="text-[12px] px-3 py-1 rounded-full bg-red-100 text-red-600 inline-flex items-center gap-1"><AlertTriangle size={12} /> {highCount} high priority</span>}
          <button onClick={() => setNewOpen(true)} data-testid="maintenance-new-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1c1c1e] text-white text-[13px] font-500 hover:bg-black transition-colors"><Plus size={15} /> New issue</button>
        </div>
      </div>

      {issues.length === 0 ? <p className="p-8 text-center text-[#8a8a8a]">No maintenance issues reported.</p> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="maintenance-board">
          {COLUMNS.map((col) => (
            <div key={col.key} className="rounded-xl bg-[#faf7f2] border border-[#f0ece5] p-3" data-testid={`maintenance-col-${col.key}`}>
              <div className="flex items-center gap-2 mb-3 px-1">
                <span className={`w-2 h-2 rounded-full ${col.color}`} />
                <p className="text-[13px] font-500 text-[#2b2b2b]">{col.label}</p>
                <span className="text-[12px] text-[#a0a0a0] ml-auto">{grouped[col.key].length}</span>
              </div>
              <div className="space-y-2 min-h-[40px]">
                {grouped[col.key].map((i) => (
                  <div key={i.id} className="p-3 rounded-lg bg-white border border-[#f0ece5]" data-testid="maintenance-card">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-[14px] font-500 text-[#2b2b2b] leading-snug">{i.title}</p>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full capitalize ${i.priority === "high" ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-600"}`}>{i.priority}</span>
                        <button onClick={() => removeIssue(i)} data-testid={`issue-delete-${i.id}`} title="Delete issue" className="text-[#c9c2b6] hover:text-red-500 transition-colors"><Trash2 size={13} /></button>
                      </div>
                    </div>
                    {i.property && <p className="text-[11px] text-[#b8935f] mt-0.5">{i.property}</p>}
                    <p className="text-[12px] text-[#8a8a8a] mt-1">{i.resident && i.resident !== "—" ? i.resident : "Office-raised"}{i.detail ? ` · ${i.detail}` : ""}</p>
                    {i.contractor && <p className="text-[11px] text-[#b8935f] mt-1 flex items-center gap-1"><HardHat size={11} /> {i.contractor}</p>}
                    <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
                      {nextStatus[i.status] && (
                        <button onClick={() => setStatus(i.id, nextStatus[i.status])} data-testid="issue-advance-btn" className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#1c1c1e] text-white text-[11px] hover:bg-black transition-colors capitalize">{nextStatus[i.status].replace("-", " ")} <ChevronRight size={12} /></button>
                      )}
                      <button onClick={() => openJob(i)} data-testid="raise-job-btn" title="Raise job to contractor" className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#e2d8c8] text-[11px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors"><HardHat size={12} /> Job</button>
                      <Select value={i.status} onValueChange={(v) => setStatus(i.id, v)}>
                        <SelectTrigger className="w-[36px] h-6 px-1.5 border-[#e2d8c8]" data-testid="issue-status-select"><span className="sr-only">status</span></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("-", " ")}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
                {grouped[col.key].length === 0 && <p className="text-[12px] text-[#c0c0c0] text-center py-4">Nothing here</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogTitle className="font-display text-[24px] font-300 text-[#2b2b2b] mb-1">Log a maintenance issue</DialogTitle>
          <DialogDescription className="text-[13px] text-[#8a8a8a] mb-4">Choose the property, describe the problem and optionally assign a contractor.</DialogDescription>
          <form onSubmit={submitNew} className="space-y-3" data-testid="maintenance-new-form">
            <input placeholder="Short title (e.g. Boiler not firing)" value={nf.title} onChange={(e) => setNf({ ...nf, title: e.target.value })} className={input} data-testid="new-issue-title" />
            <textarea placeholder="Describe the problem with the property…" value={nf.detail} onChange={(e) => setNf({ ...nf, detail: e.target.value })} rows={3} className="w-full px-3 py-2 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] resize-none" data-testid="new-issue-detail" />
            <Select value={nf.property} onValueChange={(v) => setNf({ ...nf, property: v })}>
              <SelectTrigger className="h-[44px] border-[#e2d8c8]" data-testid="new-issue-property"><SelectValue placeholder="Which property?" /></SelectTrigger>
              <SelectContent className="max-h-[240px]">{properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.address || p.title}{p.postcode ? ` · ${p.postcode}` : ""}</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <Select value={nf.priority} onValueChange={(v) => setNf({ ...nf, priority: v })}>
                <SelectTrigger className="h-[44px] border-[#e2d8c8]" data-testid="new-issue-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{[["low", "Low"], ["medium", "Medium"], ["high", "High"]].map(([v, l]) => <SelectItem key={v} value={v}>{l} priority</SelectItem>)}</SelectContent>
              </Select>
              <Select value={nf.contractor} onValueChange={(v) => setNf({ ...nf, contractor: v })}>
                <SelectTrigger className="h-[44px] border-[#e2d8c8]" data-testid="new-issue-contractor"><SelectValue placeholder="Assign to…" /></SelectTrigger>
                <SelectContent>{contractors.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.trade}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <button type="submit" disabled={creating} className="w-full h-[48px] rounded-lg bg-[#b8935f] text-white text-[15px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="new-issue-submit">{creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Log issue</button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={jobOpen} onOpenChange={setJobOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogTitle className="font-display text-[24px] font-300 text-[#2b2b2b] mb-1">Raise a Job</DialogTitle>
          <DialogDescription className="text-[13px] text-[#8a8a8a] mb-4">{jobIssue?.title} — {jobIssue?.resident}</DialogDescription>
          <form onSubmit={submitJob} className="space-y-3" data-testid="raise-job-form">
            <Select value={jf.contractorId} onValueChange={(v) => setJf({ ...jf, contractorId: v })}>
              <SelectTrigger className="h-[44px] border-[#e2d8c8]" data-testid="job-contractor-select"><SelectValue placeholder="Select contractor…" /></SelectTrigger>
              <SelectContent>{contractors.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} — {c.trade}</SelectItem>)}</SelectContent>
            </Select>
            <input placeholder="Estimated cost (£, optional)" value={jf.cost} onChange={(e) => setJf({ ...jf, cost: e.target.value })} className={input} data-testid="job-cost-input" />
            <textarea placeholder="Job description" value={jf.description} onChange={(e) => setJf({ ...jf, description: e.target.value })} rows={2} className="w-full px-3 py-2 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] resize-none" data-testid="job-desc-input" />
            <button type="submit" className="w-full h-[48px] rounded-lg bg-[#b8935f] text-white text-[15px] font-500 hover:bg-[#a67f4d] transition-colors" data-testid="job-submit-btn">Raise Job</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMaintenance;
